import React, { useState, useEffect } from 'react';
import { Send, Brain, RotateCcw, AlertTriangle, CheckCircle, Loader2, Paintbrush, HelpCircle } from 'lucide-react';
import { BACKEND_URL } from '../../config'; // Załóżmy, że config jest w głównym src

const EditorProjectSupportPanel = ({
  downloadKey,
  backendUrl = BACKEND_URL,
  tooltipsEnabled,
  pages,
  projectId,
  orderEmail = '',
  setIsAiProcessing,
  aiPreviousVersionId,
  onHelpClick, // Odbieramy nową funkcję
}) => {
  console.log("[EditorProjectSupportPanel] Otrzymany prop aiPreviousVersionId:", aiPreviousVersionId);
  const [supportOption, setSupportOption] = useState(''); // 'graphic', 'ai'
  const [clientRequestText, setClientRequestText] = useState('');
  const [clientEmail, setClientEmail] = useState(orderEmail || '');
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
  const [currentHistoryId, setCurrentHistoryId] = useState(null);
  const [previousVersionIdForAi, setPreviousVersionIdForAi] = useState(null);

  useEffect(() => {
    const fetchLatestHistoryId = async () => {
      if (!downloadKey && !projectId) return;
      const idToUse = projectId || downloadKey;
      const endpointType = projectId ? 'projects' : 'projects-key';

      try {
        const response = await fetch(`${backendUrl}/client/${endpointType}/${idToUse}/history/latest`);
        if (!response.ok) {
          throw new Error('Nie udało się pobrać najnowszej historii projektu.');
        }
        const data = await response.json();
        if (data.success && data.newestId) {
          setCurrentHistoryId(data.newestId);
        }
      } catch (error) {
        console.error("Błąd pobierania ID ostatniej historii:", error);
        setStatusMessage({ type: 'error', text: 'Nie można załadować informacji o historii projektu.' });
      }
    };
    fetchLatestHistoryId();
  }, [pages, projectId, downloadKey, backendUrl]);

  // Efekt do aktualizacji clientEmail, gdy zmieni się orderEmail
  useEffect(() => {
    if (orderEmail) {
      setClientEmail(orderEmail);
    }
  }, [orderEmail]);


  const pollInteractionStatus = (interactionId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${backendUrl}/public/ai-interaction-status/${interactionId}`);
        const result = await response.json();

        if (result.status === 'completed_success') {
          clearInterval(interval);
          setStatusMessage({ type: 'success', text: 'Projekt został pomyślnie zmodyfikowany. Odświeżanie...' });
          setTimeout(() => window.location.reload(), 1500);
        } else if (result.status === 'completed_error') {
          clearInterval(interval);
          setStatusMessage({ type: 'error', text: `Błąd przetwarzania AI: ${result.data || 'Nieznany błąd.'}` });
          setIsLoading(false);
          if (typeof setIsAiProcessing === 'function') {
            setIsAiProcessing(false);
          }
        }
        // Jeśli status to 'pending' lub inny, kontynuuj odpytywanie
      } catch (error) {
        clearInterval(interval);
        setStatusMessage({ type: 'error', text: 'Błąd sprawdzania statusu przetwarzania.' });
        setIsLoading(false);
        if (typeof setIsAiProcessing === 'function') {
          setIsAiProcessing(false);
        }
      }
    }, 3000); // Odpytuj co 3 sekundy
  };

  const handleSendRequest = async () => {
    if (!clientRequestText.trim()) {
      setStatusMessage({ type: 'error', text: 'Wpisz treść prośby.' });
      return;
    }
    setIsLoading(true);
    setStatusMessage({ type: '', text: '' });

    const isEmailRequired = !orderEmail;
    if (isEmailRequired && (!clientEmail.trim() || !clientEmail.includes('@'))) {
      setStatusMessage({ type: 'error', text: 'Podaj poprawny adres email do kontaktu.' });
      setIsLoading(false);
      return;
    }

    if (supportOption === 'ai' && typeof setIsAiProcessing === 'function') {
      setIsAiProcessing(true);
    }

    try {
      if (supportOption === 'graphic') {
        const endpoint = `${backendUrl}/client/projects/${downloadKey}/request-graphic-changes`;
        const payload = { clientRequestText, clientEmail };
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Wystąpił błąd podczas wysyłania prośby do grafika.');
        }
        setStatusMessage({ type: 'success', text: result.message });
        setClientRequestText('');
        setIsLoading(false);
      } else if (supportOption === 'ai') {
        if (!currentHistoryId) {
          throw new Error('Nie można zidentyfikować aktualnej wersji projektu.');
        }
        const endpoint = `${backendUrl}/client/projects/${downloadKey}/ai-modify`;
        const payload = { clientRequestText, previousHistoryId: currentHistoryId, clientEmail };
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          // Nawet jeśli odpowiedź nie jest 202, może zawierać ważny komunikat błędu
          throw new Error(result.message || 'Wystąpił błąd podczas wysyłania prośby AI.');
        }
        
        if (response.status === 202 && result.interactionId) {
          // Rozpocznij odpytywanie
          setStatusMessage({ type: 'info', text: 'Prośba przyjęta. Przetwarzanie AI w toku...' });
          pollInteractionStatus(result.interactionId);
        } else {
          // Obsługa nieoczekiwanej, ale udanej odpowiedzi
          throw new Error('Otrzymano nieoczekiwaną odpowiedź od serwera.');
        }
      } else {
        throw new Error('Wybierz opcję wsparcia.');
      }
    } catch (error) {
      setStatusMessage({ type: 'error', text: error.message || 'Nie udało się wysłać prośby.' });
      setIsLoading(false);
      if (typeof setIsAiProcessing === 'function') {
        setIsAiProcessing(false);
      }
    }
  };

  const handleRestoreVersion = async () => {
    if (!aiPreviousVersionId) {
      setStatusMessage({ type: 'error', text: 'Brak poprzedniej wersji do przywrócenia.' });
      return;
    }
    setIsLoading(true);
    setStatusMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${backendUrl}/client/projects/${downloadKey}/history/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ historyEntryIdToRestore: aiPreviousVersionId }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Nie udało się przywrócić poprzedniej wersji.');
      }
      setStatusMessage({ type: 'success', text: 'Poprzednia wersja projektu została przywrócona. Odświeżanie...' });
      setTimeout(() => window.location.reload(), 1500);

    } catch (error) {
      setStatusMessage({ type: 'error', text: error.message || 'Błąd podczas próby przywrócenia wersji.' });
    } finally {
      setIsLoading(false);
    }
  };


  const renderForm = () => (
    <div style={{ padding: '15px' }}>
      <textarea
        value={clientRequestText}
        onChange={(e) => setClientRequestText(e.target.value)}
        placeholder={supportOption === 'graphic' ? "Opisz, jakie zmiany chciałbyś zlecić grafikowi..." : "Opisz, jak AI ma poprawić Twój projekt..."}
        rows="5"
        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', marginBottom: '10px' }}
        disabled={isLoading}
      />
      {((supportOption === 'graphic' || supportOption === 'ai') && !orderEmail) && (
        <input
          type="email"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
          placeholder="Twój adres email do kontaktu"
          style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', marginBottom: '10px' }}
          disabled={isLoading}
        />
      )}
      <button
        onClick={handleSendRequest}
        disabled={isLoading}
        style={{
          backgroundColor: isLoading ? '#ccc' : '#007bff',
          color: 'white',
          padding: '10px 15px',
          border: 'none',
          borderRadius: '4px',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
        <span style={{ marginLeft: '8px' }}>{isLoading ? 'Wysyłanie...' : 'Wyślij prośbę'}</span>
      </button>
    </div>
  );

  return (
    <div style={{ fontFamily: "'CenturySchoolbook', sans-serif" }}>
      <div style={{ padding: '15px', borderBottom: '1px solid #eee' }}>
        <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Wsparcie Projektowe</h3>
        
        <div style={{
          padding: '15px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          backgroundColor: '#f9fafb',
          marginBottom: '15px',
          textAlign: 'center',
        }}>
          <p style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#374151', fontWeight: '500' }}>
            Chcesz samodzielnie wprowadzić zmiany?
          </p>
          <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#6b7280' }}>
            Większość elementów możesz edytować bezpośrednio na projekcie.
          </p>
          <div 
            onClick={onHelpClick}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '8px', 
              cursor: 'pointer', 
              color: '#3b82f6' 
            }}
          >
            <HelpCircle size={24} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Jak edytować projekt?</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
          <button
            onClick={() => { setSupportOption('graphic'); setStatusMessage({ type: '', text: '' }); }}
            style={{
              height: '120px',
              border: supportOption === 'graphic' ? '2px solid #3b82f6' : '1px solid #d1d5db',
              borderRadius: '8px',
              background: supportOption === 'graphic' ? 'linear-gradient(to right, #eff6ff, #dbeafe)' : '#ffffff',
              color: '#1f2937',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            disabled={isLoading}
          >
            <Paintbrush size={32} color="#3b82f6" />
            <span style={{ fontSize: '16px', fontWeight: '500' }}>Poproś grafika o zmiany</span>
          </button>
          <button
            onClick={() => { setSupportOption('ai'); setStatusMessage({ type: '', text: '' }); }}
            style={{
              height: '120px',
              border: supportOption === 'ai' ? '2px solid #8b5cf6' : '1px solid #d1d5db',
              borderRadius: '8px',
              background: supportOption === 'ai' ? 'linear-gradient(to right, #f5f3ff, #ede9fe)' : '#ffffff',
              color: '#1f2937',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            disabled={isLoading}
          >
            <Brain size={32} color="#8b5cf6" />
            <span style={{ fontSize: '16px', fontWeight: '500' }}>Popraw projekt z AI</span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              (nowy projekt w ~5 min)
            </span>
          </button>
        </div>
      </div>

      {supportOption && renderForm()}

      {statusMessage.text && (
        <div style={{
          margin: '15px',
          padding: '10px',
          borderRadius: '4px',
          backgroundColor: statusMessage.type === 'success' ? '#d4edda' : (statusMessage.type === 'error' ? '#f8d7da' : '#e2e3e5'),
          color: statusMessage.type === 'success' ? '#155724' : (statusMessage.type === 'error' ? '#721c24' : '#383d41'),
          border: `1px solid ${statusMessage.type === 'success' ? '#c3e6cb' : (statusMessage.type === 'error' ? '#f5c6cb' : '#d6d8db')}`,
          display: 'flex',
          alignItems: 'center'
        }}>
          {statusMessage.type === 'success' && <CheckCircle size={18} style={{ marginRight: '8px' }} />}
          {statusMessage.type === 'error' && <AlertTriangle size={18} style={{ marginRight: '8px' }} />}
          {statusMessage.text}
        </div>
      )}

      {(aiPreviousVersionId || previousVersionIdForAi) && (
        <div style={{ padding: '0 15px 15px 15px' }}>
          <button
            onClick={handleRestoreVersion}
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#ccc' : '#6c757d',
              color: 'white',
              padding: '10px 15px',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              marginTop: '10px'
            }}
          >
            {isLoading ? <Loader2 size={20} className="animate-spin" /> : <RotateCcw size={18} />}
            <span style={{ marginLeft: '8px' }}>Przywróć wersję sprzed edycji AI</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default EditorProjectSupportPanel;
